import React from "react";
import { avatarColors, computeAge, getInitials, sidebarPatientName } from "./patientFormRules";
import { kbdStyle, sectionTitleStyle } from "./patientFormStyles";

const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const ctrlLabel = isMac ? "⌃" : "Ctrl+";

const sidebarStyle = {
    width: "260px",
    minWidth: "260px",
    borderRight: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    background: "#f8fafc",
};

const headerStyle = {
    padding: "10px 14px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
};

const addButtonStyle = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: "8px",
    border: "none",
    background: "#6366f1",
    color: "white",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};

function itemStyle(active, pharmaValidated) {
    return {
        padding: "10px 10px",
        borderRadius: "8px",
        cursor: "pointer",
        background: pharmaValidated ? "#f0fdf4" : active ? "white" : "transparent",
        boxShadow: active ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
        borderLeft: active ? `3px solid ${pharmaValidated ? "#16a34a" : "#6366f1"}` : "3px solid transparent",
        marginBottom: "3px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        transition: "background 0.1s",
        width: "100%",
        borderTop: 0,
        borderRight: 0,
        borderBottom: 0,
        textAlign: "left",
    };
}

function itemClassName(active, pharmaValidated) {
    return `commande-editor-patient${active ? " commande-editor-patient--active" : ""}${pharmaValidated ? " commande-editor-patient--validated" : ""}`;
}

function PatientSidebarItem({ patient, index, active, onSelect, validationField }) {
    const { bg, color } = avatarColors(index);
    const pharmaValidated = !!patient[validationField];
    const age = computeAge(patient.dateNaissance);
    const traitCount = (patient.produits || []).length;

    return (
        <button
            type="button"
            className={itemClassName(active, pharmaValidated)}
            onClick={onSelect}
            style={itemStyle(active, pharmaValidated)}
        >
            <div style={{
                width: "34px", height: "34px",
                borderRadius: "50%",
                background: bg, color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "12px", fontWeight: 700,
                flexShrink: 0,
                userSelect: "none",
            }}>
                {getInitials(patient.nom, patient.prenom)}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: "13px", fontWeight: active ? 600 : 500,
                    color: active ? "#0f172a" : "#334155",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                    {sidebarPatientName(patient, index)}
                </div>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "1px" }}>
                    {age !== null && <span>{age} ans · </span>}
                    {traitCount} ttt{pharmaValidated ? " · validé" : ""}
                </div>
            </div>

            <span style={{ fontSize: "11px", color: "#cbd5e1", fontWeight: 600, flexShrink: 0 }}>
                {String(index + 1).padStart(2, "0")}
            </span>
        </button>
    );
}

function AddPatientButton({ onAdd }) {
    return (
        // Bouton sticky : suit la liste quand elle est courte, se fixe en bas si elle déborde
        <div style={{ position: "sticky", bottom: 0, paddingTop: "6px", paddingBottom: "2px", background: "#f8fafc" }}>
            <button
                className="commande-editor-add-patient"
                onClick={onAdd}
                style={addButtonStyle}
                onMouseEnter={e => e.currentTarget.style.background = "#4f46e5"}
                onMouseLeave={e => e.currentTarget.style.background = "#6366f1"}
            >
                <span>+ Ajouter un patient</span>
                <kbd style={{
                    fontSize: "11px", color: "#6366f1",
                    background: "rgba(255,255,255,0.9)", border: "none",
                    borderRadius: "4px", padding: "2px 6px", fontFamily: "inherit", fontWeight: 700,
                }}>{ctrlLabel}P</kbd>
            </button>
        </div>
    );
}

export default function PatientSidebar({
    patients,
    selectedIdx,
    onSelect,
    onAdd,
    validationField = "pharmaceuticalValidated",
}) {
    return (
        <div className="commande-editor-sidebar" style={sidebarStyle}>
            <div className="commande-editor-sidebar-header" style={headerStyle}>
                <span style={sectionTitleStyle}>
                    Patients · {patients.length}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ fontSize: "10px", color: "#94a3b8", marginRight: "2px" }}>nav</span>
                    {["J+↑", "J+↓"].map(k => <kbd key={k} style={kbdStyle}>{k}</kbd>)}
                </div>
            </div>

            <div className="commande-editor-patient-list" style={{ flex: 1, overflowY: "auto", padding: "6px 8px" }}>
                {patients.map((patient, i) => (
                    <PatientSidebarItem
                        key={i}
                        patient={patient}
                        index={i}
                        active={i === selectedIdx}
                        onSelect={() => onSelect(i)}
                        validationField={validationField}
                    />
                ))}
                <AddPatientButton onAdd={onAdd} />
            </div>
        </div>
    );
}
